import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import CategoryMultiSelect from "@/components/CategoryMultiSelect";
import { Plus, Trash2, Star, Contact } from "lucide-react";

const vcardEsc = (s) => String(s || "").replace(/([,;\\])/g, "\\$1").replace(/\n/g, "\\n");

export function saveToPhone(c, company, note) {
  const first = c.first_name || (c.name || "").split(" ")[0] || "";
  const last = c.last_name || (c.name || "").split(" ").slice(1).join(" ") || "";
  const lines = ["BEGIN:VCARD", "VERSION:3.0", `N:${vcardEsc(last)};${vcardEsc(first)};;;`, `FN:${vcardEsc(`${first} ${last}`.trim() || c.name)}`];
  if (company) lines.push(`ORG:${vcardEsc(company)}`);
  if (c.job_title) lines.push(`TITLE:${vcardEsc(c.job_title)}`);
  if (c.email) lines.push(`EMAIL;TYPE=WORK:${c.email}`);
  if (c.phone) lines.push(`TEL;TYPE=WORK,VOICE:${c.phone}`);
  if (c.whatsapp && c.whatsapp !== c.phone) lines.push(`TEL;TYPE=CELL:${c.whatsapp}`);
  if (c.phone3) lines.push(`TEL;TYPE=OTHER:${c.phone3}`);
  if (note) lines.push(`NOTE:${vcardEsc(note)}`);
  lines.push("END:VCARD");
  const blob = new Blob([lines.join("\r\n") + "\r\n"], { type: "text/vcard" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${(`${first} ${last}`.trim() || "contact").replace(/[^\w\s-]/g, "")}.vcf`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

const EMPTY = { name: "", first_name: "", last_name: "", job_title: "", email: "", phone: "", whatsapp: "", phone3: "", primary: false, always_include: false, always_include_as: "cc" };

export const ContactsEditor = ({ contacts, onChange, showAlwaysInclude = false, showDivisions = false, company = "" }) => {
  const list = contacts || [];
  const setName = (i, patch) => {
    const merged = { ...list[i], ...patch };
    merged.name = `${merged.first_name || ""} ${merged.last_name || ""}`.trim() || merged.name || "";
    onChange(list.map((c, idx) => (idx === i ? merged : c)));
  };
  const set = (i, patch) => onChange(list.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const add = () => onChange([...list, { ...EMPTY, primary: list.length === 0 }]);
  const remove = (i) => {
    const next = list.filter((_, idx) => idx !== i);
    if (next.length && !next.some((c) => c.primary)) next[0].primary = true;
    onChange(next);
  };
  const makePrimary = (i) => onChange(list.map((c, idx) => ({ ...c, primary: idx === i })));

  return (
    <div className="space-y-3" data-testid="contacts-editor">
      {!list.length && (
        <div className="rounded-lg border border-dashed border-border px-5 py-8 text-center text-sm text-muted-foreground" data-testid="contacts-empty">
          No contacts yet — add the people you deal with at this account.
        </div>
      )}
      {list.map((c, i) => (
        <div key={i} className="rounded-lg border border-border bg-slate-50/60 p-3" data-testid={`contact-row-${i}`}>
          {(c.phone || c.email || c.whatsapp) && (c.name || c.first_name) && (
            <div className="mb-2 flex justify-end">
              <button type="button" onClick={() => saveToPhone(c, company)} className="inline-flex items-center gap-1 rounded-md border border-[#1B4383]/30 bg-white px-2 py-1 text-[11px] font-semibold text-[#1B4383] hover:bg-[#E6ECF5]" data-testid={`contact-save-phone-${i}`}><Contact className="h-3.5 w-3.5" />Save To Phone Contacts</button>
            </div>
          )}
          <div className="grid gap-2.5 sm:grid-cols-2">
            <Input placeholder="First name" value={c.first_name || (c.name || "").split(" ")[0] || ""} onChange={(e) => setName(i, { first_name: e.target.value })} data-testid={`contact-first-${i}`} />
            <Input placeholder="Last name" value={c.last_name || (c.name || "").split(" ").slice(1).join(" ") || ""} onChange={(e) => setName(i, { last_name: e.target.value })} data-testid={`contact-last-${i}`} />
            <Input placeholder="Job title" value={c.job_title} onChange={(e) => set(i, { job_title: e.target.value })} data-testid={`contact-title-${i}`} />
            <Input placeholder="Email" type="email" value={c.email} onChange={(e) => set(i, { email: e.target.value })} data-testid={`contact-email-${i}`} />
            <Input placeholder="Phone" value={c.phone} onChange={(e) => set(i, { phone: e.target.value })} data-testid={`contact-phone-${i}`} />
            <Input placeholder="Second Phone / WhatsApp" value={c.whatsapp || ""} onChange={(e) => set(i, { whatsapp: e.target.value })} data-testid={`contact-whatsapp-${i}`} />
            <Input placeholder="Third Phone (optional)" value={c.phone3 || ""} onChange={(e) => set(i, { phone3: e.target.value })} data-testid={`contact-phone3-${i}`} />
          </div>
          {showDivisions && (
            <div className="mt-2.5">
              <div className="mb-1 text-xs text-slate-500">Divisions this person handles (they'll be CC'd on those RFQs)</div>
              <CategoryMultiSelect value={c.divisions || []} onChange={(v) => set(i, { divisions: v })} testid={`contact-divisions-${i}`} />
            </div>
          )}
          <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => makePrimary(i)} data-testid={`contact-primary-${i}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                  c.primary ? "bg-[#E6ECF5] text-[#1B4383]" : "text-slate-500 hover:bg-slate-200"}`}>
                <Star className={`h-3.5 w-3.5 ${c.primary ? "fill-[#1B4383]" : ""}`} />
                {c.primary ? "Primary contact" : "Set as primary"}
              </button>
              {showAlwaysInclude && (
                <label className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-slate-600 cursor-pointer hover:bg-slate-200" data-testid={`contact-always-${i}`}>
                  <input type="checkbox" checked={!!c.always_include} onChange={(e) => set(i, { always_include: e.target.checked })} className="h-3.5 w-3.5 accent-[#1B4383]" />
                  Always include on emails
                </label>
              )}
              {showAlwaysInclude && c.always_include && (
                <select value={c.always_include_as || "cc"} onChange={(e) => set(i, { always_include_as: e.target.value })}
                  className="rounded-md border border-input bg-white px-2 py-1 text-xs" data-testid={`contact-always-as-${i}`}>
                  <option value="cc">as CC</option>
                  <option value="to">as To</option>
                </select>
              )}
            </div>
            <button type="button" onClick={() => remove(i)} className="text-slate-400 hover:text-destructive" data-testid={`contact-remove-${i}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="add-contact-button">
        <Plus className="mr-2 h-4 w-4" />Add contact person
      </Button>
    </div>
  );
};

export default ContactsEditor;
